import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, PINNED_CENTER_ID } from '../firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  const loadProfile = useCallback(async (user) => {
    if (!user) {
      setProfile(null);
      return null;
    }
    const snap = await getDoc(doc(db, 'users', user.uid));
    if (!snap.exists()) throw new Error('Your portal user profile is missing. Ask an administrator to recreate it.');
    const data = { id: snap.id, ...snap.data() };
    if (PINNED_CENTER_ID && !data.isSuperAdmin && data.tenantId !== PINNED_CENTER_ID) {
      await firebaseSignOut(auth);
      throw new Error('This account belongs to a different coaching center.');
    }
    if (data.active === false) {
      await firebaseSignOut(auth);
      throw new Error('Your account is disabled.');
    }
    if (!data.isSuperAdmin && data.tenantId) {
      const [instituteSnap, platformSnap] = await Promise.all([
        getDoc(doc(db, 'institutes', data.tenantId)),
        getDoc(doc(db, 'platformSettings', 'global')),
      ]);
      const institute = instituteSnap.exists() ? instituteSnap.data() : null;
      if (!institute || !['active', 'trial'].includes(institute.status)) {
        await firebaseSignOut(auth);
        throw new Error('This coaching center is suspended or unavailable. Contact the platform administrator.');
      }
      if (platformSnap.exists() && platformSnap.data().maintenanceMode === true) {
        await firebaseSignOut(auth);
        throw new Error('The platform is temporarily in maintenance mode. Please try again later.');
      }
    }
    setProfile(data);
    return data;
  }, []);

  useEffect(() => onAuthStateChanged(auth, async (user) => {
    setLoading(true);
    setAuthError('');
    setFirebaseUser(user);
    try {
      await loadProfile(user);
    } catch (error) {
      setProfile(null);
      setAuthError(error.message || 'Unable to load user profile.');
    } finally {
      setLoading(false);
    }
  }), [loadProfile]);

  const login = useCallback(async (email, password, instituteCode = '', expectedTenantId = '') => {
    setAuthError('');
    const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
    const data = await loadProfile(credential.user);
    const requestedCode=String(instituteCode||'').trim().toUpperCase();
    const expectedTenant=String(expectedTenantId||'').trim();
    if(expectedTenant && !data?.isSuperAdmin && String(data?.tenantId||'')!==expectedTenant){await firebaseSignOut(auth);setProfile(null);throw new Error('This account belongs to a different institute.');}
    if(requestedCode && !data?.isSuperAdmin){
      const instituteSnap=await getDoc(doc(db,'institutes',data.tenantId));
      const actual=String(instituteSnap.data()?.loginCode||instituteSnap.data()?.code||'').trim().toUpperCase();
      if(!actual||actual!==requestedCode){await firebaseSignOut(auth);setProfile(null);throw new Error('Institute code does not match this account.');}
    }
    return data;
  }, [loadProfile]);

  const logout = useCallback(async () => {
    await firebaseSignOut(auth);
    setProfile(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!auth.currentUser) return null;
    await auth.currentUser.getIdToken(true);
    return loadProfile(auth.currentUser);
  }, [loadProfile]);

  const can = useCallback((permission) => {
    if (!profile) return false;
    if (profile.isSuperAdmin) return true;
    const keys = profile.permissionKeys || [];
    return keys.includes('all') || keys.includes(permission);
  }, [profile]);

  const value = useMemo(() => ({
    firebaseUser,
    profile,
    loading,
    authError,
    login,
    logout,
    can,
    refreshProfile,
    isSuperAdmin: profile?.isSuperAdmin === true,
    tenantId: profile?.tenantId || null,
  }), [firebaseUser, profile, loading, authError, login, logout, can, refreshProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
